/**
 * File: VendingMachinesVerticalPage_v01.tsx
 * Description: ReactSet vertical-format page displaying vending machine images and descriptions
 *
 * Version: v01.0
 * Created: 2026-01-29
 * Chat: https://chat.openai.com
 */

import React from "react";
import { Container, Row, Col, Card, Button } from "react-bootstrap";

// ============================================================
// ReactSet Meta
// ============================================================

const reactsetMeta = {
  component: "VendingMachinesVerticalPage",
  version: "v01.0",
  layout: "vertical",
  category: "vending",
};

// ============================================================
// Types
// ============================================================

interface VendingMachine {
  id: string;
  name: string;
  imageUrl: string;
  description: string;
  idealFor: string;
}

// ============================================================
// Data (Externalizable later)
// ============================================================

const VENDING_MACHINES: VendingMachine[] = [
  {
    id: "snack",
    name: "Snack Vending Machine",
    imageUrl: "https://images.unsplash.com/photo-1580910051074-7f36c3c30f6b",
    description:
      "Offers a wide variety of chips, candy bars, and packaged snacks. High turnover and universally popular.",
    idealFor: "Offices, schools, warehouses",
  },
  {
    id: "beverage",
    name: "Beverage Vending Machine",
    imageUrl: "https://images.unsplash.com/photo-1622445275463-afa2ab738c34",
    description:
      "Dispenses cold drinks including soda, water, sports drinks, and energy beverages.",
    idealFor: "Gyms, break rooms, manufacturing floors",
  },
  {
    id: "combo",
    name: "Combo Snack & Drink Machine",
    imageUrl: "https://images.unsplash.com/photo-1600195077075-5f65f1fdb4f4",
    description:
      "A compact all-in-one solution offering both snacks and beverages in a single unit.",
    idealFor: "Small offices, apartment buildings",
  },
  {
    id: "healthy",
    name: "Healthy Vending Machine",
    imageUrl: "https://images.unsplash.com/photo-1542838132-92c53300491e",
    description:
      "Features healthier options like protein bars, nuts, low-sugar drinks, and organic snacks.",
    idealFor: "Hospitals, wellness centers, corporate HQs",
  },
];

// ============================================================
// Component
// ============================================================

const VendingMachinesVerticalPage: React.FC = () => {
  return (
    <Container className="py-5">
      {/* Header */}
      <Row className="mb-5 text-center">
        <Col>
          <h1 className="fw-bold">Vending Machine Options</h1>
          <p className="text-muted">
            Explore different vending machine types and find the best fit for
            your location.
          </p>
        </Col>
      </Row>

      {/* Vertical Machine List */}
      <Row>
        <Col>
          {VENDING_MACHINES.map((machine) => (
            <Card key={machine.id} className="mb-4 shadow-sm">
              <Card.Img
                variant="top"
                src={machine.imageUrl}
                alt={machine.name}
                style={{ maxHeight: "320px", objectFit: "cover" }}
              />
              <Card.Body>
                <Card.Title className="fw-bold">
                  {machine.name}
                </Card.Title>
                <Card.Text>{machine.description}</Card.Text>
                <Card.Text>
                  <strong>Ideal for:</strong> {machine.idealFor}
                </Card.Text>
                <Button variant="primary">
                  Check Availability
                </Button>
              </Card.Body>
            </Card>
          ))}
        </Col>
      </Row>
    </Container>
  );
};

export default VendingMachinesVerticalPage;

/**
 * End of File
 * VendingMachinesVerticalPage_v01.tsx
 * Version: v01.0
 */
